import { useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";

import "@xyflow/react/dist/style.css";
import {
  type Node,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from "@xyflow/react";
import {
  createContext,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  usePlayoffsQueryOptions,
  usePoolNamesQueryOptions,
  useTeamMap,
} from "@/components/tournaments/context";
import type { tournamentsRouter } from "@/integrations/trpc/routers/tournaments";
import { seedPlayoffs } from "@/lib/playoffs";
import { isNotNull, isNotNullOrUndefined } from "@/utils/types";

import { MatchEdge } from "./match-edge";
import { MatchNode } from "./match-node";
import { Toolbar } from "./toolbar";

const nodeTypes = { match: MatchNode };
const edgeTypes = {
  match: MatchEdge,
};

type MatchesMap = {
  [key: number]: BracketMatch;
};

function getBracketRounds(
  map: MatchesMap,
  match: BracketMatch,
): (BracketMatch & { midx: number })[][] {
  const rounds: (BracketMatch & { midx: number })[][] = [];

  // Start with the finals match in round 0
  rounds[0] = [{ ...match, midx: 0 }];

  // Process each round to find previous matches
  let currentRoundIndex = 0;

  while (rounds[currentRoundIndex] && rounds[currentRoundIndex].length > 0) {
    const nextRoundMatches: (BracketMatch & { midx: number })[] = [];

    for (const currentMatch of rounds[currentRoundIndex]) {
      // Add team A's previous match if it exists
      if (currentMatch.teamAPreviousMatchId !== null) {
        const prevMatch = map[currentMatch.teamAPreviousMatchId];
        if (prevMatch) {
          // Derive nextMatchId without mutating the original cached object
          const nextMatchId = prevMatch.nextMatchId ?? currentMatch.id;

          nextRoundMatches.push({
            ...prevMatch,
            nextMatchId,
            midx: nextRoundMatches.length,
          });
        }
      }

      // Add team B's previous match if it exists
      if (currentMatch.teamBPreviousMatchId !== null) {
        const prevMatch = map[currentMatch.teamBPreviousMatchId];
        if (prevMatch) {
          const nextMatchId = prevMatch.nextMatchId ?? currentMatch.id;

          nextRoundMatches.push({
            ...prevMatch,
            nextMatchId,
            midx: nextRoundMatches.length,
          });
        }
      }
    }

    if (nextRoundMatches.length > 0) {
      rounds[currentRoundIndex + 1] = nextRoundMatches;
    }

    currentRoundIndex++;
  }

  return rounds;
}

function getNodesFromRounds(
  rounds: (BracketMatch & { midx: number })[][],
  measuredHeights?: Map<string, number>,
): Node<BracketMatch & { midx: number; roundIdx: number }>[] {
  const nodes: Node<BracketMatch & { midx: number; roundIdx: number }>[] = [];

  // Compute expected round sizes before reversal (rounds arrive as [finals, semis, quarters, ...first])
  const roundSizes = rounds.map((_, i) =>
    i === 0 ? 1 : rounds[i - 1]?.length * 2,
  );

  roundSizes.reverse();
  const reversedRounds = [...rounds].reverse();
  // Now index 0 = first/earliest round, last = finals

  const gap = 40; // Vertical gap between adjacent nodes in the same round
  const defaultNodeHeight = 220; // Fallback before measurement

  // Map from match ID to Y position so parents can center between children
  const yPositions = new Map<number, number>();

  for (const [ridx, round] of reversedRounds.entries()) {
    const expectedMatches = roundSizes[ridx];
    const isPlayInRound = round.length < expectedMatches;
    const nextRound = reversedRounds[ridx + 1];

    // Reassign midx for play-in rounds based on which slot they feed into
    if (isPlayInRound && nextRound) {
      for (const match of round) {
        const nextMatch = nextRound.find((m) => m.id === match.nextMatchId);

        if (match.id === nextMatch?.teamAPreviousMatchId) {
          match.midx = nextMatch.midx * 2;
        } else if (match.id === nextMatch?.teamBPreviousMatchId) {
          match.midx = nextMatch.midx * 2 + 1;
        }
      }
    }

    // Sort matches by midx for consistent top-to-bottom ordering
    const sortedRound = [...round].sort((a, b) => a.midx - b.midx);

    for (const [seqIdx, match] of sortedRound.entries()) {
      const nodeHeight =
        measuredHeights?.get(match.id.toString()) ?? defaultNodeHeight;

      // Try to center this match between its two feeder matches
      const childYs: number[] = [];
      if (match.teamAPreviousMatchId != null) {
        const cy = yPositions.get(match.teamAPreviousMatchId);
        if (cy !== undefined) childYs.push(cy);
      }
      if (match.teamBPreviousMatchId != null) {
        const cy = yPositions.get(match.teamBPreviousMatchId);
        if (cy !== undefined) childYs.push(cy);
      }

      let y: number;
      if (childYs.length >= 2) {
        // Center between the two feeder matches
        y = (childYs[0] + childYs[1]) / 2;
      } else if (childYs.length === 1) {
        // Single feeder (e.g. one side is a bye) — align with it
        y = childYs[0];
      } else {
        // No feeders (first round). Stack compactly using sequential position.
        y = seqIdx * (nodeHeight + gap);
      }

      yPositions.set(match.id, y);

      nodes.push({
        id: match.id.toString(),
        type: "match",
        position: {
          x: ridx * 800,
          y,
        },
        data: {
          ...match,
          roundIdx: ridx,
        },
        draggable: false,
        selectable: false,
      });
    }
  }

  // Post-pass: fix overlaps within each round caused by centering
  fixOverlaps(nodes, measuredHeights, gap, defaultNodeHeight);

  return nodes;
}

/**
 * After centering parents between children, some nodes in the same round
 * may overlap. This walks each round top-to-bottom and pushes nodes down
 * when they'd overlap with the previous node, then re-centers any parent
 * that was affected.
 */
function fixOverlaps(
  nodes: Node<BracketMatch & { midx: number; roundIdx: number }>[],
  measuredHeights: Map<string, number> | undefined,
  gap: number,
  defaultNodeHeight: number,
) {
  // Group nodes by round (x position)
  const byRound = new Map<number, typeof nodes>();
  for (const node of nodes) {
    const ridx = node.data.roundIdx;
    const arr = byRound.get(ridx) ?? [];
    arr.push(node);
    byRound.set(ridx, arr);
  }

  // ID-based lookup for quick centering fixups
  const nodeById = new Map(nodes.map((n) => [n.id, n]));

  // Process each round from earliest to latest
  const roundIndices = [...byRound.keys()].sort((a, b) => a - b);
  for (const ridx of roundIndices) {
    const roundNodes = byRound.get(ridx);
    if (!roundNodes) continue;
    // Sort by bracket position (midx) to preserve intended ordering.
    // Sorting by y can break ordering when single-feeder alignment
    // positions a later match above an earlier one.
    roundNodes.sort((a, b) => a.data.midx - b.data.midx);

    for (let i = 1; i < roundNodes.length; i++) {
      const prev = roundNodes[i - 1];
      const curr = roundNodes[i];
      const prevHeight = measuredHeights?.get(prev.id) ?? defaultNodeHeight;
      const minY = prev.position.y + prevHeight + gap;

      if (curr.position.y < minY) {
        curr.position.y = minY;
      }
    }

    // After pushing nodes down in this round, re-center any parent nodes
    // in the next round that reference nodes in this round
    const nextRound = byRound.get(ridx + 1);
    if (nextRound) {
      for (const parent of nextRound) {
        const childYs: number[] = [];
        if (parent.data.teamAPreviousMatchId != null) {
          const child = nodeById.get(
            parent.data.teamAPreviousMatchId.toString(),
          );
          if (child) childYs.push(child.position.y);
        }
        if (parent.data.teamBPreviousMatchId != null) {
          const child = nodeById.get(
            parent.data.teamBPreviousMatchId.toString(),
          );
          if (child) childYs.push(child.position.y);
        }
        if (childYs.length >= 2) {
          parent.position.y = (childYs[0] + childYs[1]) / 2;
        } else if (childYs.length === 1) {
          parent.position.y = childYs[0];
        }
      }
    }
  }
}

function buildNodeTree(
  map: MatchesMap,
  measuredHeights?: Map<string, number>,
): {
  nodes: Node[];
  rounds: BracketMatch[][];
} {
  const matches = Object.values(map);

  if (matches.length === 0) {
    return { nodes: [], rounds: [] };
  }
  const finals =
    matches.find(
      (match) =>
        match.nextMatchId === null &&
        isNotNullOrUndefined(match.teamAPreviousMatchId) &&
        isNotNullOrUndefined(match.teamBPreviousMatchId),
    ) ?? matches.find((match) => match.nextMatchId === null);

  if (!finals) {
    throw new Error("Could not find finals match");
  }

  const rounds = getBracketRounds(map, finals);

  const nodes = getNodesFromRounds(rounds, measuredHeights);

  // getBracketRounds returns [finals, semis, quarters, ...first] but nodes
  // are laid out left-to-right (first round on the left).  Reverse so that
  // rounds[0] = first round (leftmost) and rounds[n] = finals (rightmost),
  // matching the visual order for the toolbar buttons.
  return { nodes, rounds: [...rounds].reverse() };
}

type TournamentsOutput = inferRouterOutputs<typeof tournamentsRouter>;

export type BracketMatch = TournamentsOutput["getPlayoffs"][number];

export type BracketProps = {
  matches: BracketMatch[];
};

type BracketInteractiveState = {
  activeTeam: number | null;
  nodeIdToCenter: number | null;
};

type TeamMap = ReturnType<typeof useTeamMap>["teamMap"];

type BracketContextValue = {
  state: BracketInteractiveState;
  setState: Dispatch<SetStateAction<BracketInteractiveState>>;
  seedMap: { pool: number; seed: number }[] | null;
  poolNames: string[];
  teamMap: TeamMap;
};

const BracketContext = createContext<BracketContextValue>({
  state: {
    activeTeam: null,
    nodeIdToCenter: null,
  },
  setState: () => {},
  seedMap: null,
  poolNames: [],
  teamMap: new Map(),
});

export function useBracketContext() {
  const context = useContext(BracketContext);

  return context;
}

export function useActiveTeam() {
  const {
    state: { activeTeam },
  } = useBracketContext();

  return activeTeam;
}

export function useSetActiveTeam() {
  const { setState } = useBracketContext();

  return (activeTeam: number | null) =>
    setState((state) => ({ ...state, activeTeam }));
}

export function useSetNodeIdToCenter() {
  const { setState } = useBracketContext();

  return (nodeIdToCenter: number) =>
    setState((state) => ({ ...state, nodeIdToCenter }));
}

export function useSeedMap() {
  const { seedMap } = useBracketContext();

  return seedMap;
}

export function useBracketTeamMap() {
  const { teamMap } = useBracketContext();

  return teamMap;
}

export function usePoolNames() {
  const { poolNames } = useBracketContext();

  return poolNames;
}

function buildEdges(matches: BracketMatch[]) {
  return matches.flatMap((match) =>
    [
      match.teamAPreviousMatchId
        ? {
            id: [match.teamAPreviousMatchId, match.id].join(),
            target: match.id.toString(),
            source: match.teamAPreviousMatchId.toString(),
            sourceHandle: match.teamAId?.toString(),
            targetHandle: match.teamAId?.toString(),
            type: "match",
          }
        : null,
      match.teamBPreviousMatchId
        ? {
            id: [match.teamBPreviousMatchId, match.id].join(),
            target: match.id.toString(),
            source: match.teamBPreviousMatchId.toString(),
            sourceHandle: match.teamBId?.toString(),
            targetHandle: match.teamBId?.toString(),
            type: "match",
          }
        : null,
    ].filter(isNotNull),
  );
}

function BracketFlow({ matches }: BracketProps) {
  const matchesMap = matches.reduce<{
    [key: number]: BracketMatch;
  }>((memo, match) => {
    memo[match.id] = match;
    return memo;
  }, {});

  const { nodes: initialNodes, rounds } = buildNodeTree(matchesMap);
  const initialEdges = buildEdges(matches);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [layoutReady, setLayoutReady] = useState(false);
  const hasRepositioned = useRef(false);
  const layoutGeneration = useRef(0);
  const { fitView, getNodes, setViewport, getViewport } = useReactFlow();

  // After ReactFlow measures nodes, reposition using actual heights
  const handleNodesChange: typeof onNodesChange = useCallback(
    (changes) => {
      onNodesChange(changes);

      // Check if we got dimension changes (ReactFlow measured nodes)
      const hasDimensionChanges = changes.some(
        (c) => c.type === "dimensions" && c.dimensions,
      );

      if (hasDimensionChanges && !hasRepositioned.current) {
        // First RAF: wait for React to commit the current render so
        // node.measured heights are available in state.
        const gen = layoutGeneration.current;
        requestAnimationFrame(() => {
          if (layoutGeneration.current !== gen) return;
          setNodes((currentNodes) => {
            // Collect measured heights
            const measuredHeights = new Map<string, number>();
            let allMeasured = true;

            for (const node of currentNodes) {
              if (node.measured?.height) {
                measuredHeights.set(node.id, node.measured.height);
              } else {
                allMeasured = false;
              }
            }

            if (!allMeasured || measuredHeights.size === 0) {
              return currentNodes;
            }

            hasRepositioned.current = true;

            // Rebuild node positions using measured heights
            const freshMap = matches.reduce<MatchesMap>((memo, match) => {
              memo[match.id] = match;
              return memo;
            }, {});
            const { nodes: repositionedNodes } = buildNodeTree(
              freshMap,
              measuredHeights,
            );

            // Second RAF: wait for React to commit the repositioned nodes
            // so ReactFlow has rendered them before we fitView.
            requestAnimationFrame(() => {
              if (layoutGeneration.current !== gen) return;
              fitView({ padding: 0.15 }).then(() => {
                if (layoutGeneration.current !== gen) return;
                const allNodes = getNodes();
                if (allNodes.length === 0) {
                  setLayoutReady(true);
                  return;
                }

                // Find the topmost node position
                let minY = Number.POSITIVE_INFINITY;
                for (const n of allNodes) {
                  if (n.position.y < minY) minY = n.position.y;
                }

                const vp = getViewport();
                const topPadding = 20;
                const currentScreenY = minY * vp.zoom + vp.y;
                const shiftY = topPadding - currentScreenY;

                setViewport(
                  { x: vp.x, y: vp.y + shiftY, zoom: vp.zoom },
                  { duration: 0 },
                );
                setLayoutReady(true);
              });
            });

            return repositionedNodes;
          });
        });
      }
    },
    [
      onNodesChange,
      matches,
      setNodes,
      fitView,
      getNodes,
      getViewport,
      setViewport,
    ],
  );

  // Update nodes and edges when matches change
  useEffect(() => {
    const freshMap = matches.reduce<MatchesMap>((memo, match) => {
      memo[match.id] = match;
      return memo;
    }, {});

    const { nodes: newNodes } = buildNodeTree(freshMap);
    const newEdges = buildEdges(matches);

    layoutGeneration.current++;
    hasRepositioned.current = false;
    setLayoutReady(false);
    setNodes(newNodes);
    setEdges(newEdges);
  }, [matches, setNodes, setEdges]);

  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={containerRef}
      className="bg-content-background relative flex h-screen w-screen flex-col overflow-hidden"
    >
      <Toolbar rounds={rounds} container={containerRef}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodesChange={handleNodesChange}
          onEdgesChange={onEdgesChange}
          draggable={false}
          panOnScroll={true}
          fitView={true}
          fitViewOptions={{ padding: 0.1 }}
          minZoom={0.2}
          preventScrolling={false}
          proOptions={{ hideAttribution: true }}
          onNodeMouseEnter={() => {}}
          style={{ opacity: layoutReady ? 1 : 0 }}
        />
        {!layoutReady && (
          <div className="bg-content-background absolute inset-0 flex items-center justify-center">
            <span className="text-gray-500">Loading bracket...</span>
          </div>
        )}
      </Toolbar>
    </div>
  );
}

export function Bracket({ matches }: BracketProps) {
  const playoffsOptions = usePlayoffsQueryOptions();
  const poolNamesOptions = usePoolNamesQueryOptions();
  const { teamMap } = useTeamMap();

  const { data: poolNames = [] } = useQuery({
    ...poolNamesOptions,
    select: (data) => data.map((pool) => pool.name),
  });

  const { data: maxSeed } = useQuery({
    ...playoffsOptions,
    select: (data) => {
      let max = 0;
      for (const m of data) {
        if (m.teamASeed != null && m.teamASeed > max) max = m.teamASeed;
        if (m.teamBSeed != null && m.teamBSeed > max) max = m.teamBSeed;
      }
      return max;
    },
  });

  const seedMap = useMemo(
    () =>
      poolNames.length && maxSeed
        ? seedPlayoffs(maxSeed, poolNames.length)
        : null,
    [poolNames.length, maxSeed],
  );

  const [state, setState] = useState<BracketInteractiveState>({
    activeTeam: null,
    nodeIdToCenter: null,
  });

  const contextValue = useMemo(
    () => ({ state, setState, seedMap, poolNames, teamMap }),
    [state, seedMap, poolNames, teamMap],
  );

  return (
    <ReactFlowProvider>
      <BracketContext value={contextValue}>
        <BracketFlow matches={matches} />
      </BracketContext>
    </ReactFlowProvider>
  );
}
